import * as React from "react";
import {authorize, IWithRouterProps, Overlay, withRouter} from "@athena-portal/components";
import {PluginTemplates} from "../../../../../utils/PluginTemplates";
import * as moment from "moment";
import {
    CommonHelpers, CoverChange,
    Document,
    FalconNavigatorHeader,
    FREETEXT_DOCUMENT,
    OptionalExtras,
    ICustomerPortalService,
    NON_PRINTABLE_DOCUMENTS,
    Policy,
    POLICY_STATUS_PENDING_RENEWAL,
    productHelper,
    ProductsEnum,
    Renewal,
    Sms,
    Types,
    WELCOME_PACK,
    WOLF_CANCELLATION_EMAIL_TITLE,
    WOLF_CHARGEBACK_CANCELLATION,
    WOLF_CHARGEBACK_COVER,
    WOLF_CHARGEBACK_NO_COVER,
    WOLF_MTA_CONFIRMATION_PACK,
    WOLF_RENEWAL_ACCEPTANCE,
    WOLF_RENEWAL_CLAIMS_IMPACT,
    WOLF_RENEWAL_INVITE_PACK,
} from "@falcon/services";
import {lazyInject} from "@athena-portal/core";
import {finalize, map} from "rxjs/operators";
import FalconHeader from "../../../../../components/navigation/FalconHeader";
import FalconFooter from "../../../../../components/navigation/FalconFooter";
import {downloadDocument, trimFileExtension} from "../../../../../CommonFunctions";
import {PostRequestedDocumentsButton} from "./PostRequestedDocumentsButton";
import {mapDocumentTitles} from "../../../../../utils/DataMappers";
import {AnnualHomeDocumentListModal, latestCoverChangeDate} from "@falcon/plugin-falcon-journey";
import CommunicationList from "../../../common/documents/CommunicationList";
import {notificationTemplates} from "../../../common/documents/NotificationTemplates";
import {FalconAccordion} from "../../../../../components/FalconAccordion";
import {CommunicationType} from "../../../common/documents/CommunicationType";
import {
    filterSuppressedCancellationDocuments,
    filterSuppressedCancellationEmails,
    filterSuppressedCancellationSms
} from "../../../common/documents/CommunicationSuppression";

interface IDocumentsPageParams {
    policyNumber: string;
    [key: string]: string | undefined;
}

interface IState {
    policies?: Policy[];
    archiveDocuments: Document[];
    isDocumentDownloaded: boolean;
    day: number;
    showHistory: boolean;
    currentTransactionalDocuments: Document[];
    receiveByPostChecked: boolean | null;
    showLoading: boolean;
    coverTier: string;
    renewalProposal?: Renewal;
    renewalProposalDocuments: Document[];
    emails: Document[];
    sms: Sms[];
    isRenewed: boolean;
    addedHEDate?: Date;
    addedHLEDate?: Date;
}

@authorize
export class AnnualHomeDocumentsPagePlain extends React.Component<IWithRouterProps<IDocumentsPageParams>, IState> {

    private readonly pluginTemplates = new PluginTemplates();

    @lazyInject(Types.CustomerPortalService)
    private readonly customerPortalService: ICustomerPortalService;

    private readonly FILE_PATH: string = "path";
    private readonly FILE_NAME: string = "fileName";
    private readonly FILE_ID: string = "id";
    private readonly FILE_LABEL: string = "label";
    private readonly isBritanniaBrand: boolean = ["bn", "britannia"].includes(
        (process.env.REACT_APP_THEME ?? "").toLowerCase()
    );

    constructor(props: Readonly<IWithRouterProps<IDocumentsPageParams>>) {
        super(props);
        this.state = {
            archiveDocuments: [],
            day: 0,
            showHistory: false,
            isDocumentDownloaded: true,
            currentTransactionalDocuments: [],
            receiveByPostChecked: null,
            showLoading: true,
            coverTier: '',
            renewalProposalDocuments: [],
            emails: [],
            sms: [],
            isRenewed: false,
        };

        this.renderDocumentsByPost = this.renderDocumentsByPost.bind(this);
        this.createPDFMap = this.createPDFMap.bind(this);
        this.getDocuments = this.getDocuments.bind(this);
    }

    public componentDidMount(): void {
        this.pluginTemplates.setPageTitle("Documents and Communications");

        this.customerPortalService
            .getPolicies()
            .pipe(map((policies: Policy[]) => policies.filter((policy: Policy) => !policy.endDate || moment(policy.endDate).isSameOrAfter(moment().startOf("day")))))
            .subscribe((activePolicies: Policy[]) => {
                this.customerPortalService
                    .getExpiredPolicies(ProductsEnum.HOME)
                    .subscribe((expiredPolicies: Policy[]) => {

                        const filteredActivePolicies: Policy[] = activePolicies
                            .filter((policy: Policy) => this.filterPolicyMatchedAndNotFutureCancelled(policy))
                            .sort((a, b) => moment(b.endDate).diff(a.endDate));

                        const policies = [...(filteredActivePolicies || []), ...(expiredPolicies || [])];

                        this.setState({
                            policies,
                            coverTier: policies[0].interaction.coverTier
                        });

                        const selectedPolicy = this.getSelectedPolicy(policies);

                        this.getDocuments(policies);
                        if (!this.isBritanniaBrand) {
                            this.getSms(selectedPolicy);
                            this.customerPortalService
                                .getOptionalExtrasInformation(this.props.params.policyNumber)
                                .subscribe(
                                    (optionalExtraChanges: CoverChange[]) => {
                                        this.setState({
                                            addedHEDate: latestCoverChangeDate(optionalExtraChanges, OptionalExtras.HOME_EMERGENCY),
                                            addedHLEDate: latestCoverChangeDate(optionalExtraChanges, OptionalExtras.HOME_LEGAL_EXPENSES)
                                        });
                                    }
                                );
                        }
                    });
            });

        this.customerPortalService.getPolicyHistoryAfterRenewal()
            .pipe(finalize(() => {
                this.setState({showLoading: false});
            }))
            .subscribe((policyHistories: Policy[]) => {
                this.setState({isRenewed: policyHistories?.length > 0})
            });
    }

    public render(): React.ReactNode {
        const hasPendingRenewal: boolean = this.state?.renewalProposal?.status === POLICY_STATUS_PENDING_RENEWAL;
        return (
            <React.Fragment>
                <FalconHeader/>

                <div className="plugin">
                    <FalconNavigatorHeader
                        back={this.state.policies && `/dashboard/annualHome/${this.state.policies[0].policyNumber}`}
                        title="Your home insurance policy documents and communications"
                    />
                    {!this.state.showLoading && <div className="documents-page">
                        {!hasPendingRenewal && this.renderDocumentsPanel("Policy documents", this.state.currentTransactionalDocuments)}
                        {hasPendingRenewal && this.renderDocumentsPanelWithAccordion("Renewal policy documents", true, this.state.renewalProposalDocuments, this.state.renewalProposal?.annualHomeFixedAddons)}
                        {hasPendingRenewal && this.renderDocumentsPanelWithAccordion("Existing policy documents", false, this.state.currentTransactionalDocuments, this.state.policies?.[0].interaction.annualHomeFixedAddons)}
                        {this.state.isRenewed && <CommunicationList
                            communicationCollection={{
                                type: CommunicationType.ArchivedDocuments,
                                items: this.state.archiveDocuments
                            }}
                            header={"Documents archive"}
                            cssStyles={"document-list documents-archive"}
                            navigate={this.props.navigate}
                        />}
                        {this.renderDocumentsByPost()}
                        {<CommunicationList
                            communicationCollection={{
                                type: CommunicationType.Emails,
                                items: this.state.emails
                            }}
                            header={"Emails"}
                            cssStyles={`document-list emails-block ${this.state.sms.length ? "" : "final-communication-block"}`}
                            navigate={this.props.navigate}
                        />}
                        {<CommunicationList
                            communicationCollection={{
                                type: CommunicationType.SMS,
                                items: this.state.sms
                            }}
                            header={"SMS"}
                            cssStyles={"document-list sms-block final-communication-block"}
                            navigate={this.props.navigate}
                        />}
                    </div>}
                    {this.state.policies && this.pluginTemplates.renderBackLink("Back", false, `/dashboard/annualHome/${this.state.policies[0].policyNumber}`)}
                </div>
                <Overlay show={this.state.showLoading}/>
                <FalconFooter/>
            </React.Fragment>
        );
    }

    private renderDocumentsPanel(title: string, documents: Document[]) {
        const policy = this.state.policies?.[0];
        let startDate;
        if (policy) {
            startDate = policy.interaction?.factors?.renewalQuoteDate
                ? new Date(policy.interaction?.factors?.renewalQuoteDate) : new Date(policy.startDate);
        }
        return (
            <div className="widget documents-widget active-policy-documents-widget panel">
                <p className="active-policy-documents-title font-weight-bold text-center fs-18">{title}</p>
                <ul className="list-unstyled">
                    {this.renderTransactionalFiles(documents)}
                    <AnnualHomeDocumentListModal
                        policyDate={startDate || new Date()}
                        coverTier={this.state.renewalProposal?.coverTier ?? this.state.coverTier}
                        withTitle={false}
                        annualHomeFixedAddons={policy?.interaction?.annualHomeFixedAddons}
                        addedHEDate={this.state.addedHEDate}
                        addedHLEDate={this.state.addedHLEDate}
                    />
                </ul>
            </div>
        );
    }

    private renderDocumentsPanelWithAccordion(title: string, isShown: boolean, documents: Document[], annualHomeFixedAddons?: any): React.ReactNode {
        if (this.state.policies) {
            return (
                <div className="widget documents-widget panel existing-documents-widget-pending-renewal">
                    <ul className="list-unstyled">
                        <FalconAccordion
                            togglePositionTop={true}
                            isShown={isShown}
                            toggleShown={<div className="existing-documents-title toggle d-flex justify-content-between align-items-center accordion-collapsed"><span/>
                                <span className="text-center fs-18">{title}</span>
                                <span className="toggle-icon collapse-icon"/></div>}
                            toggleHidden={<div className="existing-documents-title toggle d-flex justify-content-between align-items-center accordion-expanded"><span/>
                                <span className="text-center fs-18">{title}</span>
                                <span className="toggle-icon expand-icon"/></div>}>
                            {this.renderTransactionalFiles(documents)}
                            <AnnualHomeDocumentListModal
                                policyDate={this.state.policies ? new Date(this.state.policies[0].startDate) : new Date()}
                                coverTier={this.state.renewalProposal?.coverTier ?? this.state.coverTier}
                                withTitle={false}
                                addedHEDate={this.state.addedHEDate}
                                addedHLEDate={this.state.addedHLEDate}
                                {...(annualHomeFixedAddons && { annualHomeFixedAddons })}
                            />
                        </FalconAccordion>
                    </ul>
                </div>
            )
        } else {
            return <></>
        }
    }

    private renderDocumentsByPost() {
        return (
            <div className="receive-by-post">
                <p className="receive-by-post-title font-weight-bold fs-18">Receive my policy documents by post</p>
                <p className="receive-by-post-description">We've gone paperless; we care about the environment and it's
                    better for everyone! But if you wish,
                    you can request below to have your documents posted to you to the address listed on your policy</p>
                {(this.state.currentTransactionalDocuments || this.state.renewalProposalDocuments) && this.state.policies &&
                    <PostRequestedDocumentsButton
                        policy={this.state.policies[0]}
                        allDocuments={CommonHelpers.getPrintableDocuments(
                            this.state.currentTransactionalDocuments.concat(this.state.renewalProposalDocuments), NON_PRINTABLE_DOCUMENTS
                        )}
                    />}
            </div>
        )
    }

    private getSelectedPolicy(policies: Policy[]): Policy | undefined {
        return policies.find((policy: Policy) =>
            policy.policyNumber === this.props.params.policyNumber
        );
    }

    private getSms(policy?: Policy): void {
        this.customerPortalService.getSms(this.props.params.policyNumber)
            .subscribe((sms: Sms[]) => {
                this.setState({
                    sms: filterSuppressedCancellationSms(sms, policy)
                });
            });
    }

    private renderTransactionalFiles(homeTransactionalDocuments: Document[]) {
        return homeTransactionalDocuments.map((document: Document, index: number) => (
            <li className="media" key={index}>
                <div className="media-body">
                    <div className="row height-0">
                        <div className="col-1"/>
                        <div className="col-11">
                            <div className="dashed-10 ml-3 mr-3"/>
                        </div>
                    </div>
                    <a className="document-download-link no-underline" onClick={this.downloadDocument(document)}
                       id={trimFileExtension(document.name)}>
                        <img className="pdf-icon active-icon" alt="pdf icon" src="/img/pdf-icon.png"/>
                        <h5 className={"pt-2 document-name"}>{document.title}</h5>
                    </a>
                </div>
            </li>
        ));
    }

    private filterPolicyMatchedAndNotFutureCancelled(policy: Policy): boolean {
        const futureCancelledPolicyBeforeInception: boolean = policy != null && moment.utc(policy.endDate, moment.ISO_8601).isSame(moment.utc(policy.startDate, moment.ISO_8601));
        const matchedPolicy: boolean = policy != null && policy.policyNumber === this.props.params.policyNumber;
        return !futureCancelledPolicyBeforeInception && matchedPolicy;
    }

    private readonly downloadDocument = (doc: Document) => (event: any): void => {
        this.setState({isDocumentDownloaded: false});
        this.customerPortalService.downloadDocument(doc.id).subscribe(
            (documentContents: ArrayBuffer) => {
                if (documentContents) {
                    downloadDocument(documentContents, doc.name);
                }
                this.setState({isDocumentDownloaded: true});
            }
        )
    }

    private getDocuments(policies: Policy[]): void {
        this.customerPortalService.getAllDocuments().subscribe(
            (documents: Document[]) => {
                this.customerPortalService.getRenewalProposal(this.props.params.policyNumber).subscribe((renewalProposal: Renewal) => {
                    this.setState({renewalProposal});
                    this.fetchDocuments(policies, renewalProposal, documents);
                });
            }
        );
    }

    private readonly fetchDocuments = (policies?: Policy[], renewalProposal?: Renewal, documents?: Document[]) => {
        const currentTransactionalDocuments: Document[] = [];
        const archiveDocuments: Document[] = [];
        let renewalProposalDocuments: Document[] = [];
        let emails: Document[] = [];

        if (policies && policies.length > 0 && documents) {
            policies.forEach((policy: Policy) => {

                const allPolicyDocs = documents.filter(doc =>
                    doc.tags.includes(`agreement_${policy.policyNumber}`));

                const renewalDocuments =
                    this.getRenewalDocumentsForPolicy(allPolicyDocs, renewalProposal);

                renewalProposalDocuments.push(...mapDocumentTitles(renewalDocuments.currentRenewalDocuments, policy));
                archiveDocuments.push(...mapDocumentTitles(renewalDocuments.archivedRenewalInvites, policy));

                const currentPolicyVersionDocs = allPolicyDocs.filter(doc => {
                    return doc.tags.filter((d: string) => d === ("agreement_version_" + policy.interaction.agreementVersion)).length > 0
                });

                const documentsToBeListed = filterSuppressedCancellationDocuments(currentPolicyVersionDocs.filter(doc =>
                    doc.name.includes(WELCOME_PACK)
                    || doc.name.includes(WOLF_MTA_CONFIRMATION_PACK)
                    || doc.name.includes(WOLF_RENEWAL_ACCEPTANCE)
                    || doc.name.includes(WOLF_CHARGEBACK_COVER)
                    || doc.name.includes(WOLF_CHARGEBACK_NO_COVER)
                    || doc.name.includes(WOLF_CHARGEBACK_CANCELLATION)
                    || doc.name.includes(WOLF_CANCELLATION_EMAIL_TITLE)
                    ),
                    policy,
                    [WOLF_CANCELLATION_EMAIL_TITLE]
                );

                const mappedCurrentDocuments = mapDocumentTitles(documentsToBeListed, policy);
                if (productHelper.includesAnnualHomeProduct([policy])) {
                    currentTransactionalDocuments.push(...mappedCurrentDocuments);
                }

                const documentsToBeListedWithoutAgreementVersion = filterSuppressedCancellationDocuments(allPolicyDocs.filter(doc =>
                    (doc.name.includes(WELCOME_PACK)
                    || doc.name.includes(WOLF_MTA_CONFIRMATION_PACK)
                    || doc.name.includes(WOLF_RENEWAL_ACCEPTANCE)
                    || doc.name.includes(WOLF_CHARGEBACK_COVER)
                    || doc.name.includes(WOLF_CHARGEBACK_NO_COVER)
                    || doc.name.includes(WOLF_CHARGEBACK_CANCELLATION)
                    || doc.name.includes(WOLF_CANCELLATION_EMAIL_TITLE)
                    || doc.name.includes(FREETEXT_DOCUMENT)
                    )
                    && !doc.tags.includes(`agreement_version_${policy.interaction.agreementVersion}`)
                    ),
                    policy,
                    [WOLF_CANCELLATION_EMAIL_TITLE]
                );

                if (productHelper.includesAnnualHomeProduct([policy])) {
                    archiveDocuments.push(...mapDocumentTitles(documentsToBeListedWithoutAgreementVersion, policy));
                }

                const matchedEmails = allPolicyDocs.filter(document => notificationTemplates.emails.find(
                    email => {
                        const isTemplateMatched: boolean = document.name === email.template;
                        if (isTemplateMatched) {
                            document.title = email.description;
                        }

                        return isTemplateMatched
                    })
                );

                emails = filterSuppressedCancellationEmails(matchedEmails, policy);
            });

            const selectedPolicy = policies.find(policy =>
                policy.policyNumber === this.props.params.policyNumber
            );

            if (selectedPolicy?.status === "RENEWAL") {
                const currentVersionTag = `agreement_version_${selectedPolicy.interaction.agreementVersion}`;

                const archivedDocumentIds = new Set(
                    archiveDocuments.map(document => document.id)
                );

                const missingArchiveDocuments = documents.filter(document => {
                    const belongsToPolicy = document.tags.includes(
                        `agreement_${selectedPolicy.policyNumber}`
                    );

                    const belongsToPreviousVersion = document.tags.some(tag =>
                            tag.startsWith("agreement_version_")
                        )
                        && !document.tags.includes(currentVersionTag);

                    const belongsToRenewal = document.tags.some(tag =>
                            /^renewal_[a-fA-F0-9]{24}$/.test(tag)
                        );

                    const isEmail = document.tags.some(tag =>
                        tag.startsWith("thunderhead_to_email_")
                    );

                    return belongsToPolicy && !isEmail && !archivedDocumentIds.has(document.id)
                        && (
                            belongsToPreviousVersion
                            || belongsToRenewal
                        );
                });

                archiveDocuments.push(
                    ...mapDocumentTitles(
                        missingArchiveDocuments,
                        selectedPolicy
                    )
                );
            }
        }

        this.setState({currentTransactionalDocuments, archiveDocuments, renewalProposalDocuments, emails});
    };

    private getRenewalDocumentsForPolicy(documents: Document[], renewalProposal?: Renewal): {
        currentRenewalDocuments: Document[];
        archivedRenewalInvites: Document[];
    } {
        let archiveInvitePdfs: Document[];

        if (renewalProposal) {
            const currentRenewalTag = `renewal_${renewalProposal._id}`;

            const renewalDocs: Document[] = documents.filter(doc =>
                doc.tags.includes(currentRenewalTag)
            );

            const renewalPdfs = renewalDocs.filter(doc =>
                doc.name.includes(WOLF_RENEWAL_CLAIMS_IMPACT));

            const invitePdfs: Document[] = renewalDocs.filter(doc =>
                doc.name.includes(WOLF_RENEWAL_INVITE_PACK));

            // if there are several invites per id, return the latest
            if (invitePdfs.length > 0) {
                const latestInvitePdf = [...invitePdfs].sort((a, b) =>
                    moment(b.date).diff(moment(a.date)))[0];
                renewalPdfs.push(latestInvitePdf)
            }

            archiveInvitePdfs = documents.filter(doc =>
                doc.name.includes(WOLF_RENEWAL_INVITE_PACK)
                && !doc.tags.includes(currentRenewalTag)
            );

            return {
                currentRenewalDocuments: [...renewalPdfs].reverse(),
                archivedRenewalInvites: this.keepLatestDocumentPerRenewalId(archiveInvitePdfs)
            };
        }

        archiveInvitePdfs = documents.filter(doc => doc.name.includes(WOLF_RENEWAL_INVITE_PACK));

        return {
            currentRenewalDocuments: [],
            archivedRenewalInvites: this.keepLatestDocumentPerRenewalId(archiveInvitePdfs)
        };
    }

    private keepLatestDocumentPerRenewalId(documents: Document[]): Document[] {
            const latestByRenewalId = new Map<string, Document>();

            documents.forEach(doc => {
                const renewalTag = this.getRenewalIdTag(doc);

                if (!renewalTag) {
                    return;
                }

                const latestDocForThisRenewal = latestByRenewalId.get(renewalTag);

                if (!latestDocForThisRenewal || moment(doc.date).isAfter(moment(latestDocForThisRenewal.date))) {
                    latestByRenewalId.set(renewalTag, doc);
                }
            });
        return Array.from(latestByRenewalId.values());
        }

    private getRenewalIdTag(doc: Document): string | undefined {
        return doc.tags.find((tag: string) =>
            /^renewal_[a-fA-F0-9]{24}$/.test(tag)
        );
    }

    private createPDFMap(path: string, fileName: string, id: string, label: string): Map<string, string> {
        return new Map()
            .set(this.FILE_PATH, path)
            .set(this.FILE_NAME, fileName)
            .set(this.FILE_ID, id)
            .set(this.FILE_LABEL, label);
    }
}

export const AnnualHomeDocumentsPage = withRouter<{}, IDocumentsPageParams>(AnnualHomeDocumentsPagePlain)
